import { HStack, Select, Skeleton, Stack, Text, createListCollection, useSelectContext } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { type IndicatorResponsiblePublic, type UserPublic, UsersService } from '@/client';

type User = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
};

const SelectValueItem = () => {
  const { t } = useTranslation();
  const select = useSelectContext();
  const items = select.selectedItems as Array<IndicatorResponsiblePublic>;

  if (!items || items.length === 0) {
    return <Text color="fg.muted">{t('user.noSelection')}</Text>;
  }

  return (
    <Select.ValueText placeholder={t('user.select')}>
      <HStack>
        {items.length === 1 ? (
          <Stack gap="0">
            <Text fontWeight="medium">{`${items[0].first_name} ${items[0].last_name}`}</Text>
            <Text color="fg.muted" textStyle="sm">
              {items[0].email}
            </Text>
          </Stack>
        ) : (
          <Text fontWeight="medium">{items.map((user) => `${user.first_name} ${user.last_name}`).join(', ')}</Text>
        )}
      </HStack>
    </Select.ValueText>
  );
};

function getUsersQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['users', { slug }],
    queryFn: async () =>
      await UsersService.readUsers({
        companySlug: slug,
      }),
  };
}

export const SelectUsers = ({
  value,
  onChange,
  slug,
}: {
  value: string[];
  onChange: (value: IndicatorResponsiblePublic[]) => void;
  slug: string;
}) => {
  const { data: usersData, isLoading } = useQuery({
    ...getUsersQueryOptions({ slug }),
  });

  const usersCollection = createListCollection({
    items:
      usersData?.data
        .map((user: UserPublic) => ({
          id: user.id,
          first_name: user.first_name ?? '',
          last_name: user.last_name ?? '',
          email: user.email ?? '',
        }))
        .sort((a, b) => a.email.localeCompare(b.email)) || [],
    itemToString: (item: User) => `${item.first_name} ${item.last_name}`,
    itemToValue: (item: User) => item.id.toString(),
  });
  return (
    <>
      {isLoading ? (
        <Skeleton height="8" />
      ) : (
        <Select.Root
          multiple
          collection={usersCollection}
          size="sm"
          w={'full'}
          value={value}
          onValueChange={(e) => onChange(usersCollection.items.filter((el) => e.value.includes(el.id.toString())))}
          positioning={{ sameWidth: true }}
        >
          <Select.Control>
            <Select.Trigger>
              <SelectValueItem />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {usersCollection.items.map((user: User) => (
                <Select.Item item={user} key={user.email}>
                  <Stack gap="0">
                    <Text fontWeight="medium">{`${user.first_name} ${user.last_name}`}</Text>
                    <Text color="fg.muted" textStyle="sm">
                      {user.email}
                    </Text>
                  </Stack>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      )}
    </>
  );
};

export default SelectUsers;
